import { ReactNode, useEffect, useCallback, useRef, RefCallback, useSyncExternalStore, forwardRef, useImperativeHandle } from 'react'

import sticky, { stuckAttributeName, shadowAttributeName } from './sticky-class'
import useID from './use-id'


const subsribe = x => sticky.subscribe(x)

type Props = {
  children: (ref: RefCallback<any>, stuck: boolean, shadow: boolean) => ReactNode
  applyToChildrenTh?: boolean
  stickyBottom?: boolean
  resetShift?: boolean // сброс смещения на уровне на 0 (не реализовано для stickyBottom)
  skipShift?: boolean // пропуск смещения на уровне (не реализовано для stickyBottom)
  updateKey?: Object | Array<any>
  disabled?: boolean
}

const Sticky = forwardRef(({
  children,
  applyToChildrenTh,
  stickyBottom,
  resetShift,
  skipShift,
  updateKey,
  disabled,
}: Props, ref) => {
  const { getID } = useID(stickyBottom)

  const refElement = useRef<HTMLElement | null>(null)

  useImperativeHandle(ref, () => {
    return refElement.current
  })

  const handleRef = useCallback((el: HTMLElement | null) => {
    if (disabled) return
    if (el) {
      const ID = getID()
      sticky.add(el, ID, { applyToChildrenTh, resetShift, skipShift })
    }
    if (refElement.current) {
      sticky.remove(refElement.current)
    }
    refElement.current = el
  }, [applyToChildrenTh, getID, resetShift, skipShift, disabled])

  const handleChangeStickyState = useCallback(() => {
    const el = refElement.current
    return !!el && el.hasAttribute(stuckAttributeName)
  }, [])

  const handleChangeShadowState = useCallback(() => {
    const el = refElement.current
    return !!el && el.hasAttribute(shadowAttributeName)
  }, [])

  const stuck = useSyncExternalStore(subsribe, handleChangeStickyState)
  const shadow = useSyncExternalStore(subsribe, handleChangeShadowState)

  useEffect(() => {
    return () => {
      if (refElement.current) {
        sticky.remove(refElement.current)
      }
    }
  }, [])

  useEffect(() => {
    if (updateKey && refElement.current) {
      sticky.updateStyles(refElement.current, getID())
    }
  }, [updateKey]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {children(handleRef, stuck, shadow)}
    </>
  )
})

export default Sticky
